import React, { useState, useEffect } from 'react';
import { Modal, Spin, message, Input, Row, Col, Pagination, Empty, Card, Tabs, Tag, Upload } from 'antd';
import { SearchOutlined, PictureOutlined, CloudOutlined, GlobalOutlined, UserOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';
import { mediaAPI } from '../utils/api';

const { Search } = Input;

// Diccionario español → inglés para búsquedas
const TRANSLATION_DICT = {
  // Términos religiosos
  'iglesia': 'church',
  'iglesias': 'churches',
  'cruz': 'cross',
  'cruces': 'crosses',
  'jesús': 'jesus',
  'cristo': 'christ',
  'cristiano': 'christian',
  'santo': 'saint',
  'santa': 'saint',
  'santos': 'saints',
  'biblia': 'bible',
  'oración': 'prayer',
  'oraciones': 'prayers',
  'rezar': 'pray',
  'rezo': 'prayer',
  'fe': 'faith',
  'dios': 'god',
  'señor': 'lord',
  'altar': 'altar',
  'capilla': 'chapel',
  'catedral': 'cathedral',
  'templo': 'temple',
  'sacerdote': 'priest',
  'cura': 'priest',
  'padre': 'father priest',
  'monje': 'monk',
  'fraile': 'friar',
  'religioso': 'religious',
  'religiosa': 'nun',
  'hermana': 'sister nun',
  'ángel': 'angel',
  'cielo': 'heaven',
  'paz': 'peace',
  'amor': 'love',
  'esperanza': 'hope',
  'caridad': 'charity',
  'virgen': 'virgin',
  'maría': 'mary',
  'misa': 'mass',
  'eucaristía': 'eucharist',
  'comunión': 'communion',
  'confesión': 'confession',
  'bautismo': 'baptism',
  'matrimonio': 'wedding',
  'funeral': 'funeral',
  'procesión': 'procession',
  'bendición': 'blessing',
  'milagro': 'miracle',
  'espíritu': 'spirit',
  'alma': 'soul',
  'pecado': 'sin',
  'perdón': 'forgiveness',
  'salvación': 'salvation',
  'gracia': 'grace',
  
  // Términos generales
  'joven': 'young',
  'jóvenes': 'young people',
  'niño': 'child',
  'niña': 'girl child',
  'niños': 'children',
  'bebé': 'baby',
  'anciano': 'elderly',
  'familia': 'family',
  'familias': 'families',
  'gente': 'people',
  'persona': 'person',
  'personas': 'people',
  'hombre': 'man',
  'mujer': 'woman',
  'madre': 'mother',
  // 'padre' ya definido arriba (religioso)
  'abuelo': 'grandfather',
  'abuela': 'grandmother',
  'hermano': 'brother',
  // 'hermana' ya definido arriba (religioso)
  'hijo': 'son',
  'hija': 'daughter',
  
  // Naturaleza
  'naturaleza': 'nature',
  'montaña': 'mountain',
  'montañas': 'mountains',
  'mar': 'sea ocean',
  'océano': 'ocean',
  'río': 'river',
  'lago': 'lake',
  // 'cielo' ya definido arriba (religioso)
  'nube': 'cloud',
  'nubes': 'clouds',
  'sol': 'sun',
  'luna': 'moon',
  'estrella': 'star',
  'estrellas': 'stars',
  'flores': 'flowers',
  'flor': 'flower',
  'árbol': 'tree',
  'árboles': 'trees',
  'bosque': 'forest',
  'campo': 'field countryside',
  'jardín': 'garden',
  'planta': 'plant',
  'agua': 'water',
  
  // Emociones y conceptos
  'felicidad': 'happiness',
  'feliz': 'happy',
  'alegría': 'joy',
  'tristeza': 'sadness',
  'triste': 'sad',
  // 'esperanza' ya definido arriba (religioso)
  'miedo': 'fear',
  'valentía': 'courage',
  'fuerza': 'strength',
  'debilidad': 'weakness',
  
  // Social
  'pobreza': 'poverty',
  'pobre': 'poor',
  'pobres': 'poor people',
  'riqueza': 'wealth',
  'rico': 'rich',
  'hambre': 'hunger',
  'sed': 'thirst',
  'enfermedad': 'disease illness',
  'enfermo': 'sick',
  'salud': 'health',
  'medicina': 'medicine',
  'hospital': 'hospital',
  'doctor': 'doctor',
  'enfermera': 'nurse',
  'refugiado': 'refugee',
  'inmigrante': 'immigrant',
  'guerra': 'war',
  'justicia': 'justice',
  'injusticia': 'injustice',
  'libertad': 'freedom',
  
  // Educación y trabajo
  'luz': 'light',
  'mano': 'hand',
  'manos': 'hands',
  'libro': 'book',
  'libros': 'books',
  'estudiante': 'student',
  'estudiantes': 'students',
  'escuela': 'school',
  'colegio': 'school',
  'universidad': 'university',
  'educación': 'education',
  'maestro': 'teacher',
  'profesor': 'teacher professor',
  'aprender': 'learn',
  'enseñar': 'teach',
  'trabajo': 'work',
  'trabajador': 'worker',
  'empleo': 'employment job',
  
  // Comunidad y servicio
  'comunidad': 'community',
  'vecino': 'neighbor',
  'pueblo': 'village town',
  'ciudad': 'city',
  'aldea': 'village',
  'ayuda': 'help',
  'ayudar': 'help',
  'servicio': 'service',
  'servidor': 'servant',
  'voluntario': 'volunteer',
  'voluntarios': 'volunteers',
  'donación': 'donation',
  'solidaridad': 'solidarity',
  'cooperación': 'cooperation',
  'unión': 'unity union'
};

// Función para traducir términos de búsqueda
const translateSearchTerm = (term) => {
  if (!term || term.trim() === '') return term;
  
  const words = term.toLowerCase().trim().split(/\s+/);
  const translatedWords = words.map(word => {
    // Quitar acentos para buscar en diccionario
    const normalizedWord = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return TRANSLATION_DICT[normalizedWord] || word;
  });
  
  return translatedWords.join(' ');
};

const MediaGalleryModal = ({ visible, onClose, onSelectImage }) => {
  const [activeTab, setActiveTab] = useState('bunny');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Estados para Bunny CDN
  const [bunnyImages, setBunnyImages] = useState([]);
  const [bunnyFiltered, setBunnyFiltered] = useState([]);
  const [bunnySearch, setBunnySearch] = useState('');
  const [bunnyPage, setBunnyPage] = useState(1);
  const [bunnyTotal, setBunnyTotal] = useState(0);

  // Estados para Pexels
  const [pexelsImages, setPexelsImages] = useState([]);
  const [pexelsSearch, setPexelsSearch] = useState('');
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsTotal, setPexelsTotal] = useState(0);
  const [pexelsCached, setPexelsCached] = useState(false);
  const [translatedTerm, setTranslatedTerm] = useState('');

  // Estados para Upload
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);

  const pageSize = 18; // 3 filas x 6 columnas

  // Cargar imágenes de Bunny al abrir modal o cambiar página
  useEffect(() => {
    if (visible && activeTab === 'bunny') {
      loadBunnyImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, activeTab, bunnyPage]);

  // Filtrar imágenes de Bunny por búsqueda local
  useEffect(() => {
    if (bunnySearch.trim() === '') {
      setBunnyFiltered(bunnyImages);
    } else {
      const filtered = bunnyImages.filter(img =>
        img.name.toLowerCase().includes(bunnySearch.toLowerCase())
      );
      setBunnyFiltered(filtered);
    }
  }, [bunnySearch, bunnyImages]);

  // Buscar en Pexels cuando cambia el término de búsqueda
  useEffect(() => {
    if (activeTab === 'pexels' && pexelsSearch.trim().length >= 3) {
      const timer = setTimeout(() => {
        searchPexels();
      }, 500); // Debounce de 500ms
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pexelsSearch, pexelsPage, activeTab]);

  const loadBunnyImages = async () => {
    setLoading(true);
    try {
      const response = await mediaAPI.getImages('images', bunnyPage, pageSize);
      
      if (response.success) {
        setBunnyImages(response.data.images);
        setBunnyFiltered(response.data.images);
        setBunnyTotal(response.data.total);
      } else {
        message.error('Error al cargar las imágenes');
      }
    } catch (error) {
      console.error('Error loading Bunny images:', error);
      message.error('Error al cargar la galería de Bunny CDN');
    } finally {
      setLoading(false);
    }
  };

  const searchPexels = async () => {
    if (pexelsSearch.trim().length < 3) {
      setPexelsImages([]);
      setTranslatedTerm('');
      return;
    }

    setLoading(true);
    try {
      // Traducir término de búsqueda
      const searchTerm = translateSearchTerm(pexelsSearch);
      const wasTranslated = searchTerm !== pexelsSearch;
      
      if (wasTranslated) {
        setTranslatedTerm(searchTerm);
      } else {
        setTranslatedTerm('');
      }

      const response = await mediaAPI.searchPexels(searchTerm, pexelsPage, pageSize);
      
      if (response.success) {
        setPexelsImages(response.data.photos);
        setPexelsTotal(response.data.total);
        setPexelsCached(response.cached || false);
      } else {
        if (response.error.includes('bloqueada')) {
          message.error('Búsqueda bloqueada: contiene palabras no permitidas');
        } else {
          message.error('Error al buscar en Pexels');
        }
        setPexelsImages([]);
      }
    } catch (error) {
      console.error('Error searching Pexels:', error);
      message.error('Error al buscar en Pexels');
      setPexelsImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleSelectImage = () => {
    if (selectedImage) {
      // Para imágenes de Bunny, usar directamente la URL
      if (activeTab === 'bunny') {
        onSelectImage(selectedImage.url);
        handleClose();
      }
      // Para Pexels, necesitamos descargar y subir a Bunny primero
      else if (activeTab === 'pexels') {
        handleSelectPexelsImage(selectedImage);
      }
      // Para Upload, la imagen ya está en Bunny CDN
      else if (activeTab === 'upload') {
        onSelectImage(selectedImage.url);
        handleClose();
      }
    }
  };

  const handleSelectPexelsImage = async (pexelsImage) => {
    const hide = message.loading('Guardando imagen...', 0);
    
    try {
      // Descargar de Pexels y subir a Bunny CDN
      const response = await mediaAPI.downloadFromPexels({
        imageUrl: pexelsImage.url,
        photographer: pexelsImage.photographer,
        photographer_url: pexelsImage.photographer_url,
        pexels_id: pexelsImage.id,
        alt: pexelsImage.alt,
        width: pexelsImage.width,
        height: pexelsImage.height
      });

      hide();

      if (response.success) {
        message.success('Imagen guardada correctamente');
        // Usar la URL de Bunny CDN
        onSelectImage(response.data.imageUrl);
        handleClose();
      } else {
        throw new Error(response.error || 'Error al descargar imagen');
      }
    } catch (error) {
      hide();
      console.error('Error downloading from Pexels:', error);
      message.error('Error al guardar la imagen');
    }
  };

  const handleClose = () => {
    setSelectedImage(null);
    setBunnySearch('');
    setPexelsSearch('');
    setBunnyPage(1);
    setPexelsPage(1);
    setActiveTab('bunny');
    setTranslatedTerm('');
    setUploadedFile(null);
    setUploadedUrl(null);
    onClose();
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedImage(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderBunnyTab = () => (
    <div>
      {/* Barra de búsqueda local */}
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Buscar por nombre de archivo..."
          prefix={<SearchOutlined />}
          value={bunnySearch}
          onChange={(e) => setBunnySearch(e.target.value)}
          allowClear
        />
      </div>

      {/* Grid de imágenes */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>
            Cargando desde Bunny CDN...
          </div>
        </div>
      ) : bunnyFiltered.length === 0 ? (
        <Empty
          description="No hay imágenes disponibles"
          style={{ padding: '60px 0' }}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ minHeight: 400 }}>
            {bunnyFiltered.map((image) => (
              <Col xs={12} sm={8} md={6} lg={4} key={image.name}>
                <Card
                  hoverable
                  cover={
                    <div
                      style={{
                        height: 160,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  }
                  bodyStyle={{
                    padding: 6,
                    backgroundColor: selectedImage?.name === image.name ? '#e6f7ff' : '#fff',
                    borderTop: selectedImage?.name === image.name ? '3px solid #1890ff' : 'none'
                  }}
                  onClick={() => handleImageClick(image)}
                >
                  <div style={{ fontSize: 10 }}>
                    <div
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: selectedImage?.name === image.name ? 'bold' : 'normal',
                        color: selectedImage?.name === image.name ? '#1890ff' : '#333'
                      }}
                      title={image.name}
                    >
                      {image.name}
                    </div>
                    <div style={{ color: '#999', marginTop: 2 }}>
                      {formatFileSize(image.size)}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Paginación */}
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <Pagination
              current={bunnyPage}
              total={bunnyTotal}
              pageSize={pageSize}
              onChange={(page) => {
                setBunnyPage(page);
                setSelectedImage(null);
              }}
              showSizeChanger={false}
              showTotal={(total) => `${total} imágenes`}
              size="small"
            />
          </div>
        </>
      )}
    </div>
  );

  const renderPexelsTab = () => (
    <div>
      {/* Barra de búsqueda en Pexels */}
      <div style={{ marginBottom: 16 }}>
        <Search
          placeholder="Buscar imágenes gratuitas (español o inglés)..."
          prefix={<SearchOutlined />}
          value={pexelsSearch}
          onChange={(e) => setPexelsSearch(e.target.value)}
          allowClear
          enterButton="Buscar"
          onSearch={searchPexels}
        />
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {translatedTerm && (
            <Tag color="blue">
              🔄 Buscando: "{translatedTerm}"
            </Tag>
          )}
          {pexelsCached && (
            <Tag color="green">
              ⚡ Resultados en caché (30 min)
            </Tag>
          )}
        </div>
      </div>

      {/* Grid de imágenes */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>
            Buscando en Pexels...
          </div>
        </div>
      ) : pexelsSearch.trim().length < 3 ? (
        <Empty
          description="Escribe al menos 3 caracteres para buscar imágenes en Pexels"
          style={{ padding: '60px 0' }}
        />
      ) : pexelsImages.length === 0 ? (
        <Empty
          description="No se encontraron imágenes para esta búsqueda"
          style={{ padding: '60px 0' }}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ minHeight: 400 }}>
            {pexelsImages.map((image) => (
              <Col xs={12} sm={8} md={6} lg={4} key={image.id}>
                <Card
                  hoverable
                  cover={
                    <div
                      style={{
                        height: 160,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={image.thumbnail}
                        alt={image.alt || 'Imagen de Pexels'}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <Tag 
                        color="green" 
                        style={{ 
                          position: 'absolute', 
                          top: 4, 
                          right: 4, 
                          fontSize: 9,
                          padding: '0 4px'
                        }}
                      >
                        Pexels
                      </Tag>
                    </div>
                  }
                  bodyStyle={{
                    padding: 6,
                    backgroundColor: selectedImage?.id === image.id ? '#e6f7ff' : '#fff',
                    borderTop: selectedImage?.id === image.id ? '3px solid #1890ff' : 'none'
                  }}
                  onClick={() => handleImageClick(image)}
                >
                  <div style={{ fontSize: 10 }}>
                    <div
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: selectedImage?.id === image.id ? 'bold' : 'normal'
                      }}
                      title={image.photographer}
                    >
                      <UserOutlined style={{ marginRight: 2 }} />
                      {image.photographer}
                    </div>
                    <div style={{ color: '#999', marginTop: 2 }}>
                      {image.width} × {image.height}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Paginación */}
          {pexelsTotal > pageSize && (
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <Pagination
                current={pexelsPage}
                total={pexelsTotal}
                pageSize={pageSize}
                onChange={(page) => {
                  setPexelsPage(page);
                  setSelectedImage(null);
                }}
                showSizeChanger={false}
                showTotal={(total) => `${total} imágenes`}
                size="small"
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  // Función para subir imagen desde dispositivo
  const handleImageUpload = async (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
    if (!isJpgOrPng) {
      message.error('Solo puedes subir archivos JPG, PNG o WebP');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('La imagen debe ser menor a 5MB');
      return false;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
    
      const token = localStorage.getItem('cms_token');
      
      const response = await fetch('https://api.appagustinos.es/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
    
      const result = await response.json();
      
      if (result.success) {
        setUploadedUrl(result.data.imageUrl);
        setUploadedFile(file);
        setSelectedImage({ url: result.data.imageUrl, name: file.name });
        message.success('Imagen subida exitosamente');
      } else {
        message.error(result.error || 'Error al subir imagen');
      }
    } catch (error) {
      message.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }

    return false;
  };

  const renderUploadTab = () => (
    <div>
      <Upload.Dragger
        name="file"
        multiple={false}
        beforeUpload={handleImageUpload}
        showUploadList={false}
        accept="image/*"
        disabled={uploading}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">
          Haz clic o arrastra una imagen aquí
        </p>
        <p className="ant-upload-hint">
          Soporta JPG, PNG, WebP. Máximo 5MB.
        </p>
      </Upload.Dragger>

      {uploading && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#666' }}>
            Subiendo imagen...
          </div>
        </div>
      )}

      {uploadedUrl && !uploading && (
        <div style={{ marginTop: 24 }}>
          <Card
            cover={
              <div style={{ 
                padding: 16, 
                display: 'flex', 
                justifyContent: 'center',
                backgroundColor: '#fafafa'
              }}>
                <img
                  src={uploadedUrl}
                  alt="Vista previa"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    objectFit: 'contain'
                  }}
                />
              </div>
            }
          >
            <Card.Meta
              title="✅ Imagen lista para usar"
              description={
                <div>
                  <div><strong>Nombre:</strong> {uploadedFile?.name}</div>
                  <div><strong>Tamaño:</strong> {(uploadedFile?.size / 1024).toFixed(2)} KB</div>
                </div>
              }
            />
          </Card>
        </div>
      )}
    </div>
  );

  const renderSelectedInfo = () => {
    if (!selectedImage) return null;

    if (activeTab === 'bunny') {
      return (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: '#f0f7ff',
            borderRadius: 6,
            border: '1px solid #91d5ff'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#1890ff' }}>
            ✓ Imagen seleccionada (Bunny CDN):
          </div>
          <div style={{ fontSize: 11 }}>
            <div><strong>Nombre:</strong> {selectedImage.name}</div>
            <div><strong>Tamaño:</strong> {formatFileSize(selectedImage.size)}</div>
            <div><strong>Fecha:</strong> {formatDate(selectedImage.dateCreated)}</div>
          </div>
        </div>
      );
    } else if (activeTab === 'pexels') {
      return (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: '#f6ffed',
            borderRadius: 6,
            border: '1px solid #b7eb8f'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#52c41a' }}>
            ✓ Imagen seleccionada (Pexels):
          </div>
          <div style={{ fontSize: 11 }}>
            <div><strong>Fotógrafo:</strong> {selectedImage.photographer}</div>
            <div><strong>Dimensiones:</strong> {selectedImage.width} × {selectedImage.height}</div>
            {selectedImage.alt && <div><strong>Alt:</strong> {selectedImage.alt}</div>}
            <div style={{ marginTop: 4, fontSize: 10, color: '#666' }}>
              📸 Imagen de Pexels (licencia gratuita)
            </div>
          </div>
        </div>
      );
    } else if (activeTab === 'upload') {
      return (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: '#fff7e6',
            borderRadius: 6,
            border: '1px solid #ffd591'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#fa8c16' }}>
            ✓ Imagen subida:
          </div>
          <div style={{ fontSize: 11 }}>
            <div><strong>Nombre:</strong> {selectedImage.name}</div>
            <div><strong>URL:</strong> {selectedImage.url}</div>
            <div style={{ marginTop: 4, fontSize: 10, color: '#666' }}>
              📤 Imagen subida a Bunny CDN
            </div>
          </div>
        </div>
      );
    }
  };

  const tabItems = [
    {
      key: 'bunny',
      label: (
        <span>
          <CloudOutlined /> Mi Galería (Bunny CDN)
        </span>
      ),
      children: renderBunnyTab()
    },
    {
      key: 'pexels',
      label: (
        <span>
          <GlobalOutlined /> Buscar en Pexels
        </span>
      ),
      children: renderPexelsTab()
    },
    {
      key: 'upload',
      label: (
        <span>
          <UploadOutlined /> Subir desde Dispositivo
        </span>
      ),
      children: renderUploadTab()
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PictureOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          <span>Galería de Medios</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      onOk={handleSelectImage}
      okText="Seleccionar imagen"
      cancelText="Cancelar"
      width={1200}
      okButtonProps={{ disabled: !selectedImage }}
      style={{ top: 20 }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      {renderSelectedInfo()}
    </Modal>
  );
};

export default MediaGalleryModal;
